import { useEffect, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { Storefront } from "@phosphor-icons/react";
import { Badge, Button, EmptyState, PageHeader, Pager, SearchInput, Select, Table, TBody, TD, TDNum, TH, THead, TR, formatCount, sortBy, statusTone } from "@ghezaa/ui";
import { listSearch, rememberListSearch, useCursorPages, useDebounced, type ListSearch } from "@ghezaa/shell";
import { useCustomers, useMe } from "@/api/queries";
import { blockedWord, day, inCurrency, moneyCell } from "@/lib/format";
import { useCurrentMarket } from "@/lib/market";

const PAGE = 50;
/** The key under which this list remembers its search, so a customer's page can link back to the same place. */
export const CUSTOMERS_LIST = "customers";

export type CustomersSearch = ListSearch & { salesperson?: string; silent?: boolean };

export function customersSearch(search: Record<string, unknown>): CustomersSearch {
  return {
    ...listSearch(search),
    ...(typeof search.salesperson === "string" && search.salesperson !== "" ? { salesperson: search.salesperson } : {}),
    ...(search.silent === true || search.silent === "true" ? { silent: true } : {}),
  };
}

/**
 * The market's customers as BC has them, with where each stands: every book for someone who sees the team's, their own
 * codes' otherwise. Each row opens the customer's page with its figures and outlets.
 */
export function CustomersPage() {
  const market = useCurrentMarket();
  const me = useMe(market.code);
  const search = useSearch({ strict: false }) as CustomersSearch;
  const navigate = useNavigate();
  const [q, setQ] = useState(search.q ?? "");
  const typed = useDebounced(q.trim());

  const setSearch = (patch: CustomersSearch) => void navigate({ to: ".", search: (prev: Record<string, unknown>) => ({ ...prev, ...patch }) as CustomersSearch, replace: true });

  useEffect(() => {
    if ((search.q ?? "") !== typed) setSearch({ q: typed || undefined, cursor: undefined, last: undefined });
  }, [typed]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => rememberListSearch(CUSTOMERS_LIST, search), [search]);

  const sort = search.sort ?? "";
  const pages = useCursorPages({
    key: `customers:${market.code}:${search.q ?? ""}:${search.salesperson ?? ""}:${search.silent ? "silent" : ""}:${sort}`,
    cursor: search.cursor,
    take: PAGE,
    onCursor: (cursor) => setSearch({ cursor, last: undefined }),
  });
  const customers = useCustomers(market.code, { q: search.q, salesperson: search.salesperson, silent: search.silent, sort: search.sort, cursor: search.cursor, take: PAGE });
  const rows = customers.data?.items ?? [];
  const total = customers.data?.total ?? null;
  const filtered = Boolean(search.q || search.salesperson || search.silent);
  const team = me.data?.team ?? false;
  const unlinked = me.data && !team && me.data.codes.length === 0;

  const header = (column: string, first: "asc" | "desc" = "asc") => {
    const s = sortBy(sort, column, first);
    return { direction: s.direction, onSort: () => setSearch({ sort: s.next, cursor: undefined, last: undefined }) };
  };

  const scope = team ? `in ${market.name}` : "in your book";
  const description =
    total === null ? undefined : filtered ? `${formatCount(total)} ${total === 1 ? "customer matches" : "customers match"}.` : `${formatCount(total)} ${total === 1 ? "customer" : "customers"} ${scope}.`;

  if (unlinked) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title="Customers" />
        <EmptyState
          icon={Storefront}
          title="Your account is not linked to a salesperson code yet"
          description="Your customers are the ones BC gives your salesperson code. Ask a Sales admin to link your code to you on the People page."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="Customers" description={description} />

      <div className="flex flex-wrap items-center gap-2">
        <SearchInput placeholder="Search name, number or city" aria-label="Search customers" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        {(me.data?.salespeople.length ?? 0) > 1 && (
          <Select aria-label="Salesperson" value={search.salesperson ?? ""} onChange={(e) => setSearch({ salesperson: e.target.value || undefined, cursor: undefined, last: undefined })} className="w-auto">
            <option value="">Every salesperson</option>
            {me.data!.salespeople.map((s) => (
              <option key={s.code} value={s.code}>
                {s.name || s.code}
              </option>
            ))}
            {team && <option value="-">No salesperson</option>}
          </Select>
        )}
        <Select aria-label="Standing" value={search.silent ? "silent" : ""} onChange={(e) => setSearch({ silent: e.target.value === "silent" || undefined, cursor: undefined, last: undefined })} className="w-auto">
          <option value="">Any customer</option>
          <option value="silent">Silent</option>
        </Select>
        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setQ("");
              setSearch({ q: undefined, salesperson: undefined, silent: undefined, cursor: undefined, last: undefined });
            }}
          >
            Clear filters
          </Button>
        )}
      </div>

      {customers.isError && <p className="bg-danger-soft px-3 py-2 text-sm text-danger">Could not load customers: {customers.error.message}</p>}
      {customers.data && rows.length === 0 ? (
        <EmptyState
          icon={Storefront}
          title={filtered ? "No customers match" : "No customers yet"}
          description={filtered ? "Try other filters, or clear them." : market.company ? "BC's last read had no customers for this book." : "Link the market to its Business Central company first."}
        />
      ) : (
        customers.data && (
          <div>
            <Table>
              <THead>
                <TR>
                  <TH sort={header("name")}>Customer</TH>
                  <TH from="md" sort={header("no")}>Number</TH>
                  {team && <TH from="lg">Salesperson</TH>}
                  <TH from="md" className="whitespace-nowrap" sort={header("last", "desc")}>Last invoice</TH>
                  <TH from="lg" className="text-end whitespace-nowrap" sort={header("revenue", "desc")}>{inCurrency("12 months", market.currency)}</TH>
                  <TH className="text-end whitespace-nowrap" sort={header("balance", "desc")}>{inCurrency("Balance", market.currency)}</TH>
                  <TH from="md" className="text-end whitespace-nowrap" sort={header("overdue", "desc")}>{inCurrency("Overdue", market.currency)}</TH>
                  <TH from="xl" className="text-end whitespace-nowrap" sort={header("limit", "desc")}>{inCurrency("Credit limit", market.currency)}</TH>
                </TR>
              </THead>
              <TBody>
                {rows.map((c) => {
                  const blocked = blockedWord(c.blocked);
                  const second = [c.searchName, c.city].filter(Boolean).join(" · ");
                  return (
                    <TR key={c.no} linked data-state={search.last === c.no ? "selected" : undefined}>
                      <TD className="w-full max-w-0">
                        <span className="flex flex-wrap items-center gap-1">
                          <Link from="/$market" to="/$market/customers/$no" params={{ no: c.no }} className="truncate underline-offset-2 hover:underline">
                            <bdi>{c.name || c.no}</bdi>
                          </Link>
                          {c.silent && <Badge tone={statusTone("silent")}>Silent</Badge>}
                          {blocked && <Badge tone={statusTone("blocked")}>{blocked}</Badge>}
                        </span>
                        {second && <span className="block truncate text-xs text-ink-3">{second}</span>}
                        <span className="block truncate text-xs text-ink-3 md:hidden">
                          <span className="font-mono">{c.no}</span>
                          {team && c.salespersonName ? ` · ${c.salespersonName}` : ""}
                          {c.lastInvoiceOn ? ` · last ${day(c.lastInvoiceOn)}` : ""}
                        </span>
                      </TD>
                      <TD from="md" className="whitespace-nowrap font-mono text-xs">{c.no}</TD>
                      {team && <TD from="lg" className="whitespace-nowrap">{c.salespersonName || <span className="text-ink-3">None</span>}</TD>}
                      <TD from="md" className="whitespace-nowrap text-xs tabular-nums">{day(c.lastInvoiceOn)}</TD>
                      <TDNum from="lg">{moneyCell(c.revenue365)}</TDNum>
                      <TDNum>{moneyCell(c.balance)}</TDNum>
                      <TDNum from="md">{moneyCell(c.overdue)}</TDNum>
                      <TDNum from="xl">{moneyCell(c.creditLimit)}</TDNum>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
            <Pager
              from={pages.from}
              shown={rows.length}
              total={total}
              hasPrevious={pages.hasPrevious}
              hasNext={Boolean(customers.data.next)}
              onPrevious={pages.previous}
              onNext={() => customers.data?.next && pages.next(customers.data.next)}
              loading={customers.isFetching}
              className="mt-3"
            />
          </div>
        )
      )}
    </div>
  );
}
